// Item picture, full size (2026-09-22). One lightbox shared by every screen that shows a thumbnail:
// the catalog list, the item card, the item picker, MR/PO/GRN lines and the composer sync review.
// The picture itself is always the composer's; nothing here edits or uploads.
import React, { useState } from 'react';
import { Modal } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

// Kept local on purpose: table-cells/ItemCell imports ItemThumb, which imports this file.
const MONO = "'JetBrains Mono', ui-monospace, Menlo, monospace";
const AR_FONT = "'Noto Naskh Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export interface PictureLightboxProps {
  open: boolean;
  onClose: () => void;
  /** Presigned GET (catalog) or an object URL (staged composer batch). */
  url?: string | null;
  itemId?: number | null;
  code?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  /** File size in bytes, as the catalog reports it. */
  size?: number | null;
  /** When the composer last set this picture, if the caller knows it. */
  date?: string | null;
}

const metaLine = (w: number | null, h: number | null, size?: number | null, date?: string | null) => {
  const parts: string[] = [];
  if (w && h) parts.push(`${w} × ${h}`);
  if (size != null) parts.push(`${Math.max(1, Math.round(size / 1024)).toLocaleString('en-US')} KB`);
  parts.push(date ? `from the composer, ${dayjs(date).format('D MMM YYYY')}` : 'from the composer');
  return parts.join(' · ');
};

const PictureLightbox: React.FC<PictureLightboxProps> = ({
  open,
  onClose,
  url,
  itemId,
  code,
  description,
  descriptionAr,
  size,
  date,
}) => {
  const [dim, setDim] = useState<{ w: number | null; h: number | null }>({ w: null, h: null });

  // The modal is a React portal rendered from inside a table row: without this, a click on the mask
  // (to close) bubbles through React's tree to the row's onClick and opens the item underneath.
  const swallow = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <div onClick={swallow} onMouseDown={swallow} onDoubleClick={swallow} onKeyDown={swallow}>
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      width="min(1200px, 92vw)"
      maskClosable
      title={null}
      closeIcon={null}
      styles={{
        content: { background: '#0f1116', padding: 14, borderRadius: 6 },
        mask: { background: 'rgba(8,10,14,.72)' },
        body: { padding: 0 },
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 220px',
          gap: 14,
          alignItems: 'start',
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', justifyContent: 'center' }}>
          {url ? (
            <img
              src={url}
              alt={description || code || 'Item picture'}
              onLoad={(e) => {
                const img = e.currentTarget;
                setDim({ w: img.naturalWidth || null, h: img.naturalHeight || null });
              }}
              style={{ maxWidth: '100%', maxHeight: '78vh', borderRadius: 4, display: 'block' }}
            />
          ) : (
            <div style={{ color: '#c9cdd8', fontSize: 13, padding: 40 }}>No picture.</div>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#c9cdd8', display: 'grid', gap: 6, alignContent: 'start' }}>
          {code && (
            <span style={{ font: `600 13px ${MONO}`, color: '#fff', wordBreak: 'break-all' }}>{code}</span>
          )}
          {description && <span style={{ color: '#e6e8f0' }}>{description}</span>}
          {descriptionAr && (
            <span
              dir="rtl"
              style={{ fontFamily: AR_FONT, unicodeBidi: 'plaintext', textAlign: 'right', fontSize: 13 }}
            >
              {descriptionAr}
            </span>
          )}
          <span>{metaLine(dim.w, dim.h, size, date)}</span>
          {itemId ? (
            <Link
              to={`/catalog/${itemId}`}
              onClick={onClose}
              style={{
                justifySelf: 'start',
                marginTop: 4,
                padding: '4px 10px',
                border: '1px solid #4a5062',
                borderRadius: 4,
                color: '#fff',
                fontSize: 13,
                textDecoration: 'none',
              }}
            >
              Open item card
            </Link>
          ) : null}
          <span style={{ color: '#7d8496', marginTop: 4 }}>
            Managed in the composer. Replace or remove it there and push again.
          </span>
        </div>
      </div>
    </Modal>
    </div>
  );
};

export default PictureLightbox;
